import { useState } from "react";
import { endOfDay, format } from "date-fns";
import { DateRangePicker } from "@circle-react/components/SettingsApp/AnalyticsV2/DateRangePicker";
import type { RangeSelectOption } from "@circle-react/components/SettingsApp/AnalyticsV2/DateRangePicker";
import { predefinedRangePickerOptions } from "@circle-react/components/SettingsApp/AnalyticsV2/DateRangePicker/predefinedRangePickerOptions";
import {
  USAGE_SECTIONS,
  UsageContextProvider,
} from "@circle-react/providers/UsageContext";
import { LocalDateGroupingDropdown } from "./LocalDateGroupingDropdown";

const dateToString = (date: Date) => format(date, "yyyy-MM-dd");

// Default to "last_3_months" which matches the backend default
const defaultOption =
  predefinedRangePickerOptions.find(
    option => option.value === "last_3_months",
  ) || predefinedRangePickerOptions[2];

interface DateSelectorProps {
  onChange: (startDate: string, endDate: string, grouping: string) => void;
}

export const DateSelector = ({ onChange }: DateSelectorProps) => {
  const [selectedRange, setSelectedRange] =
    useState<RangeSelectOption>(defaultOption);
  const [dateGrouping, setDateGrouping] = useState<string>("weekly");

  const handleDateChange = (
    selectedDates: [Date, Date],
    option: RangeSelectOption,
  ) => {
    const [startDate, endDate] = selectedDates;
    const startDateString = dateToString(startDate);
    const endDateString = dateToString(endDate);

    setSelectedRange(option);
    onChange(startDateString, endDateString, dateGrouping);
  };

  const handleDateGroupingChange = (newGrouping: string) => {
    setDateGrouping(newGrouping);
    const [startDate, endDate] = selectedRange.onSelect();
    const startDateString = dateToString(startDate);
    const endDateString = dateToString(endDate);
    onChange(startDateString, endDateString, newGrouping);
  };

  const getDefaultDateRange = () => selectedRange.onSelect();

  return (
    <UsageContextProvider section={USAGE_SECTIONS.ADMIN}>
      <div className="mb-6 flex flex-col gap-y-3 md:flex-row md:items-center md:justify-end md:gap-x-4">
        <div className="w-full md:w-auto">
          <DateRangePicker
            defaultDateRange={getDefaultDateRange()}
            onChange={handleDateChange}
            maxDate={endOfDay(new Date())}
            predefinedRange={selectedRange.value}
            predefinedRangePickerOptions={predefinedRangePickerOptions}
          />
        </div>
        <div className="w-full md:w-auto">
          <LocalDateGroupingDropdown
            onDateGroupingChange={handleDateGroupingChange}
          />
        </div>
      </div>
    </UsageContextProvider>
  );
};
